import { useMutation, useQuery } from '@apollo/client';
import { getColumns } from '@nile/client_management/src/Containers/InvoiceListColumns';
import ClientContext from '@nile/client_management/src/Contexts/ClientContext';
import InvoiceTableContext from '@nile/client_management/src/Contexts/InvoiceTableContext';
import {
  checkDynamicQuery,
  generateOptions,
  getAccessor,
  getAttributeName,
  getDefaultValue,
  getDynamicQuery,
  getInvoiceFields,
  getQueryVariables,
  getType,
  isBold,
  isEditable,
  isFilterableCol,
  isSortable
} from '@nile/client_management/src/Pebbles/InvoiceContainer';
import {
  ALL_TABLES,
  AppContext,
  DateTimeFormatter,
  EventEmitter,
  EventEmitterWithIndex,
  getAllColumnWidth,
  getColumnWidth,
  getTranslatedText,
  saveColumnWidth,
  SkeletonTableLoading,
  SuperPop,
  toast,
  useIsAuthorisedHook,
  isAuthorised,
  getTableRowCount,
  saveTableRowCount,
  SuperTable,
  getCurrentUserHook,
  getDefaultRowCount,
  useUserMSPCheckHook,
  getUnifiedConditions,
  useSuperListHook,
  DynamicWidgets
} from '@nile/components';
import SuperList from '@nile/components/src/Components/SuperList/SuperList';
import {
  AccountClient,
  GET_CLIENT_INVOICE_SCHEDULE,
  GET_INTEGRATIONS_BY_IDS,
  GET_INVOICE_LIST,
  PUSH_INVOICE,
  SEND_INVOICES
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { DateTimeCell } from '@nile/mangifera/src/Components/Ticket';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import { SVGIcon } from '@superops/super-icons';
import { TextStyle } from '@superops/ui-design-system';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import CurrencyCell from './CurrencyCell';
import InvoiceActionCell from './InvoiceActionCell';
import InvoiceEmailStatusCell from './InvoiceEmailStatusCell';
import InvoiceIntegrationStatusCell from './InvoiceIntegrationStatusCell';
import InvoiceStatusCell from './InvoiceStatusCell';
import { PaymentSummaryCell } from '@nile/client_management/src/Pages/Invoice/PaymentSummaryCell';
import CustomFieldCell from './CustomFieldCell.tsx';

function CurrencyCellWithPaymentError(props) {
  return (
    <div className="fx g5 items-center">
      <CurrencyCell {...props} />
    </div>
  );
}

const Container = styled.div`
  .table-ctr {
    height: ${({ showWidgets }) =>
      showWidgets ? 'calc(100% - 122px)' : '100%'};
  }
`;

const InvoiceSchedule = styled.div`
  background-color: #f7f7fd;
  width: 100%;
  height: 50px;
  min-height: 50px;
  .schedule {
    display: grid;
    grid-template-columns: 20px auto auto;
    gap: 10px;
    align-items: center;
  }
`;

function InvoiceContainer(props) {
  const {
    intl,
    match,
    flags,
    defaultCondition,
    hideActions,
    tableType,
    defaultViewId,
    appendViewIdToListInfo = true,
    advanceFilterCondition: advanceFilterConditionProp,
    hideColumns = []
  } = props;

  const tableName = ALL_TABLES.invoice;
  const dynamicPaginationFlag = flags?.dynamicPaginationInvoice;

  const columnWidthObject = getAllColumnWidth(tableName);

  const DEFAULT_SORT = [
    {
      attribute: 'invoiceDate',
      order: 'DESC'
    }
  ];

  const SECONDARY_SORT = { attribute: 'createdTime', order: 'DESC' };

  const isInvoiceSettingsViewAuthorised = useIsAuthorisedHook(
    'invoiceSettings.view'
  );

  // const isInvoiceCustomFieldsAuthorised = useIsAuthorisedHook(
  //   'billingCustomFields.view'
  // );
  const isClientCustomFieldsAuthorised = useIsAuthorisedHook(
    'clientCustomFields.view'
  );

  const { id, sectionId } = match.params;
  const viewId = defaultViewId || sectionId || id;

  const context = useContext(ClientContext);
  const ticketContext = useContext(TicketContext);
  const appContext = useContext(AppContext);
  const invoiceFilterContext = useContext(InvoiceTableContext);
  const invoiceFilterContextRef = useRef(0);
  invoiceFilterContextRef.current = invoiceFilterContext;
  const [listLoading, setListLoading] = useState(true);
  const [clientCustomFields, setClientCustomFields] = useState([]);

  const {
    tableCondition,
    updateTableFilterCondition,
    advanceFilterCondition,
    records,
    setRecords,
    updateAdvanceFilterCondition
  } = useSuperListHook({
    viewId,
    tableName
  });

  useEffect(() => {
    if (!advanceFilterConditionProp) return;
    updateAdvanceFilterCondition({
      joinOperator: 'AND',
      operands: advanceFilterConditionProp
    });
  }, [advanceFilterConditionProp]);

  const {
    loading: integrationsLoading,
    data: integrations,
    error: integrationsError
  } = useQuery(GET_INTEGRATIONS_BY_IDS, {
    client: AccountClient,
    variables: {
      integrationIds: ['QUICK_BOOKS_ONLINE', 'XERO', 'QUICK_BOOKS_DESKTOP']
    },
    fetchPolicy: 'network-only'
  });

  const {
    loading: paymentIntegrationsLoading,
    data: paymentIntegrations,
    error: paymentIntegrationsError
  } = useQuery(GET_INTEGRATIONS_BY_IDS, {
    client: AccountClient,
    variables: {
      integrationIds: ['STRIPE']
    },
    fetchPolicy: 'network-only'
  });

  const [pushToThirdParty] = useMutation(PUSH_INVOICE, {
    client: AccountClient
  });

  const [sendInvoice] = useMutation(SEND_INVOICES, {
    client: AccountClient
  });

  const [columnGroup, setColumnGroup] = useState(null);
  const [data, setData] = useState([]);

  const { columns: cols, loading: columnLoading } = getColumns({
    viewId: viewId,
    module: 'invoice'
  });

  useEffect(() => {
    if (!columnLoading && !integrationsLoading) generateColumns();
  }, [columnLoading, cols, integrationsLoading]);

  // const [modalActive, setModalActive] = useState(false);
  // const [clientSecret, setClientSecret] = useState(null);

  // const handleModalChange = useCallback(() => setModalActive(!modalActive), [
  //   modalActive
  // ]);

  // useEffect(() => {
  //   if (!modalActive) {
  //     setClientSecret(null);
  //   }
  // }, [modalActive]);
  /**
   * Default options for the list info to use in table
   */
  // const [options, setOptions] = useState(DEFAULT_OPTIONS);
  // const optionsByRef = useRef(null);
  // optionsByRef.current = options;

  // const [listInfo, setListInfo] = useState({});
  // const listInfoByRef = useRef(null);
  // listInfoByRef.current = listInfo;

  // const viewIdChanged = useRef(true);

  const updateData = (allData, item, field, value) => {
    const updatedData = cloneDeep(allData);
    const itemIndex = allData.findIndex((el) => el.itemId === item.itemId);
    updatedData[itemIndex] = {
      ...updatedData[itemIndex],
      [field]: value
    };
    setData(updatedData);
    context.updateContext('invoiceAuditData', updatedData);
  };

  const getActions = (col) => {
    switch (col.name) {
      case 'displayId':
      case 'invoiceId':
        return {
          onClick: (item) => {
            ticketContext.openTab({
              module: 'INVOICE',
              data: item
            });
          }
        };
      case 'pushedToIntegratedAppStatus':
      case 'actions':
        return {
          onClick: (meta, integrationType, callback) => {
            pushToThirdParty({
              variables: {
                clientId: meta.client.accountId,
                invoiceId: meta.invoiceId,
                integrationType: integrationType || 'QUICK_BOOKS_ONLINE'
              }
            })
              .then((res) => {
                if (res) {
                  toast(
                    'success',
                    'app.toast.invoice.pushTothirdParty.success'
                  );
                  callback && callback(false);
                }
              })
              .catch((err) => {
                callback && callback(false);
              });
          }
        };
      // case 'payments':
      //   return {
      //     onClick: (meta) => {
      //       initInvoicePayment({
      //         variables: {
      //           invoiceId: meta.invoiceId
      //         }
      //       }).then((res) => {
      //         handleModalChange();
      //         if (res?.data?.initInvoicePayment) {
      //           setClientSecret(res.data.initInvoicePayment.client_secret);
      //         }
      //       });
      //     }
      //   };
      case 'emailOperationalStatus':
      case 'sendInvoice':
        return {
          onClick: (meta, integrationType, callback) => {
            sendInvoice({
              variables: {
                input: [
                  {
                    invoiceId: meta.invoiceId
                  }
                ]
              }
            })
              .then((res) => {
                if (res) {
                  toast('success', 'app.toast.invoice.sentToclient.success');
                  callback(false);
                }
              })
              .catch((err) => {
                callback(false);
              });
          }
        };
      default:
        return {
          updateData: updateData
        };
    }
  };

  async function generateColumns() {
    setListLoading(true);
    const allowPushToThirdParty =
      integrations?.getIntegrationsByIds?.length > 0 &&
      integrations?.getIntegrationsByIds?.findIndex(
        (el) =>
          el.integrationId === 'QUICK_BOOKS_DESKTOP' ||
          el.integrationId === 'QUICK_BOOKS_ONLINE' ||
          el.integrationId === 'XERO'
      ) !== -1;

    cols.forEach((col) => {
      if (
        col?.isCustom &&
        col?.name?.split('.')?.length > 1 &&
        col?.name?.split('.')?.[0] === 'client' &&
        !col?.selected &&
        clientCustomFields.includes(col?.name?.split('.')?.[1])
      ) {
        const field = col?.name?.split('.')?.[1];
        setClientCustomFields((prev) => {
          return prev.filter((el) => el !== field);
        });
      }
    });
    let columns = cols
      ?.filter((col) => {
        if (hideColumns.includes(col.name)) {
          return false;
        }
        if (
          allowPushToThirdParty &&
          appContext.accountType !== 'CLIENT' &&
          !hideActions
        ) {
          return !!col?.selected;
        }

        return !!col?.selected && col?.name !== 'pushedToIntegratedAppStatus';
      })
      .map((col, idx) => {
        if (
          col?.isCustom &&
          col?.name?.split('.')?.length > 1 &&
          col?.name?.split('.')?.[0] === 'client'
        ) {
          const field = col?.name?.split('.')?.[1];
          setClientCustomFields((prev) => [...new Set([...prev, field])]);
        }
        return {
          Header: getTranslatedText(intl, col?.label),
          header: getTranslatedText(intl, col?.label),
          accessor: getAccessor(col),
          accessorKey: getAccessor(col),
          size: getColumnWidth(columnWidthObject, col?.name),
          // minWidth: col.name === 'subject' ? 400 : 50,
          // width: col.name === 'invoiceDescription' ? 400 : 150,
          type: getType(col),
          id: col?.name,
          disableResizing: false,
          className: 'sticky',
          disableSortBy: !isSortable(col),
          editable: isEditable(col),
          disableFilters: !isFilterableCol(col),
          dataType:
            col?.name === 'invoiceDate' || col?.name === 'dueDate'
              ? 'date'
              : null,
          helpers: {
            getDefaultValue,
            checkDynamicQuery,
            getDynamicQuery,
            generateOptions: (args1, args2) =>
              generateOptions(args1, args2, intl),
            updateConditions: updateTableFilterCondition
          },
          actions: getActions(col),
          field:
            col?.name === 'emailOperationalStatus' ||
            col?.name === 'pushedToIntegratedAppStatus'
              ? { name: 'approval' }
              : col,
          isBold: isBold(col),
          showError: col?.name === 'statusEnum',
          integrationType:
            integrations?.getIntegrationsByIds?.[0]?.integrationId || 'DEFAULT'
        };
        // columnGroup[0].columns.push(column);
      });

    columns.length > 0 &&
      columns.push({
        Header: ' ',
        header: ' ',
        accessor: ' ',
        accessorKey: ' ',
        width: 50,
        size: 50,
        id: 'recordPayment',
        pinned: 'right',
        type: 'PaymentSummary',
        action: true,
        disableSortBy: true,
        disableFilters: true,
        // actions: getActions({ name: 'approval' }),
        // field: { name: 'approval' },
        enablePin: true,
        defaultPin: 'right',
        disableUnpin: true
      });

    setListLoading(false);
    setColumnGroup(columns);
  }

  const showBulkSelect = useUserMSPCheckHook() && !hideActions;

  const getDefaultCondition = () => {
    if (sectionId && id) {
      return {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'client.accountId',
            operator: 'is',
            value: id
          }
        ]
      };
    }
    if (defaultCondition) {
      return defaultCondition;
    }
    return null;
  };

  const DEFAULT_CONDITION = getDefaultCondition();

  const defaultColumnCells = {
    Action: InvoiceActionCell,
    EmailStatus: InvoiceEmailStatusCell,
    IntegrationStatus: InvoiceIntegrationStatusCell,
    Currency: CurrencyCellWithPaymentError,
    Status: InvoiceStatusCell,
    DateTime: DateTimeCell,
    PaymentSummary: PaymentSummaryCell,
    CustomFieldCell: CustomFieldCell
  };

  useEffect(() => {
    EventEmitterWithIndex.subscribe('refetchInvoiceColumns', generateColumns);
    return function cleanup() {
      EventEmitterWithIndex.unsubscribe('refetchInvoiceColumns');
    };
  }, []);

  const additionalInvoiceVariables = useMemo(() => {
    let additionalVariables = {};
    if (clientCustomFields?.length) {
      additionalVariables = {
        clientCustomFields: clientCustomFields
      };
    }
    return additionalVariables;
  }, [clientCustomFields]);

  const appendOperands = (base, current) => {
    const additionalConditionParams =
      sectionId && id
        ? [
            {
              attribute: 'client.accountId',
              operator: 'is',
              value: id
            }
          ]
        : [];
    return base.concat(current, additionalConditionParams);
  };

  if (integrationsLoading || paymentIntegrationsLoading) {
    return <SkeletonTableLoading />;
  }

  return (
    <Container className="flex fcol fhp" showWidgets={sectionId === '32' && id}>
      {sectionId === '32' && id && (
        <div className="pt20">
          <DynamicWidgets
            widgetGroup="invoiceListView"
            additionalFilterConditions={appendOperands([], [])}
          />
        </div>
      )}
      <div className="table-ctr">
        <SuperList
          records={records}
          setRecords={setRecords}
          columns={columnGroup}
          data={data}
          setData={setData}
          checkBoxSize="lg"
          padded="lg"
          defaultColumnCells={defaultColumnCells}
          isLoading={listLoading}
          noBulkSelect={!showBulkSelect}
          fullHeight
          getAttributeName={getAttributeName}
          emptyState={{
            nodataImage: `${envConfig.imageRoot}svg/Invoice.svg`,
            heading: getTranslatedText(intl, 'invoice.list.noData.heading'),
            description: getTranslatedText(
              intl,
              'invoice.list.noData.description'
            )
          }}
          cardTable={!!sectionId}
          dynamicRowCount={dynamicPaginationFlag}
          columnWidthPersonalization
          saveColumnWidth={saveColumnWidth}
          tableName={tableName}
          idKey="invoiceId"
          maintainSelection
          enableSelectAll
          client={AccountClient}
          defaultCondition={DEFAULT_CONDITION}
          defaultSort={DEFAULT_SORT}
          secondarySort={SECONDARY_SORT}
          accessorKey="getInvoiceList"
          dataKey="invoices"
          viewId={viewId}
          appendViewId
          tableCondition={tableCondition}
          advanceFilterCondition={advanceFilterCondition}
          appendViewIdToListInfo={appendViewIdToListInfo}
          additionalVariables={additionalInvoiceVariables}
          queryGenerator={{
            operation: 'getInvoiceList',
            getVariables: getQueryVariables,
            getFields: () => {
              return getInvoiceFields(clientCustomFields);
            }
          }}
        />
      </div>
    </Container>
  );
}

export default injectIntl(withRouter(withLDConsumer()(InvoiceContainer)));
